import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Checkbox, RadioGroup, Radio, FormControlLabel } from "@mui/material";
import Spinner from "../components/common/Spinner";
import { ERROR_MSG, S3_URL } from "../services/Constants";
import { getAllFacilities } from "../services/sendingFacilities-service";
import { Row, Col, Button, ListGroup, Card } from "react-bootstrap";
import { fetchStatisticsResponse } from "../services/dashboard-statistics-services";
import { FixedSizeList } from "react-window";
import moment from "moment";
import { validateAndSanitizeUrl } from "../util/common";

const DashboardChartMessageStatisticsByFacility = (props) => {
  const [statisticsDataByFacility, setStatisticsDataByFacility] = useState();
  const [facilityData, setFacilityData] = useState([]);
  const [alertMessage, setAlertMessage] = useState();
  const [checkedValues, setCheckedValues] = useState([]);
  const [radioOptionButton, setRadioOptionButton] = useState("1");

  const getFacilities = async () => {
    let response = await getAllFacilities();
    if (response.ok) {
      let data = await response.json();
      setFacilityData(data);
    } else {
      let error = await response.json();
      setAlertMessage(error?.description);
    }
  };

  const handleCheckboxChange = (event) => {
    setAlertMessage("");
    const value = event.target.value;
    const updatedCheckedValues = new Set(checkedValues);

    if (updatedCheckedValues.has(value)) {
      updatedCheckedValues.delete(value);
    } else {
      updatedCheckedValues.add(value);
    }

    setCheckedValues(Array.from(updatedCheckedValues));
  };

  const handleRadioButtonChange = (event) => {
    setAlertMessage();
    setRadioOptionButton(event.target.value);
  };

  const handleClear = () => {
    setAlertMessage();
    setCheckedValues([]);
    setRadioOptionButton("");
  };

  const getStatisticsDataBySendingFacility = async (
    radioOptionButton,
    checkedValues
  ) => {
    let uri =
      "messagesBySendingFacility/" + radioOptionButton + "?id=" + checkedValues;
    let response = await fetchStatisticsResponse(uri);
    if (response.ok) {
      let data = await response.json();
      if (Array.isArray(data)) {
        setStatisticsDataByFacility(data);
      } else setAlertMessage(ERROR_MSG);
    } else {
      let error = await response.json();
      setAlertMessage(error?.description);
    }
  };

  const handleExecute = () => {
    getStatisticsDataBySendingFacility(radioOptionButton, checkedValues);
  };

  const statisticsDataXAxisName = (value) => {
    return value.substring(0, 5);
  };

  useEffect(() => {
    getFacilities();
  }, []);

  const showSpinner = () => {
    return (
      <div
        className="spinner"
        style={{
          width: "500px",
          height: "290px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: "0px",
        }}
      >
        <div className="bounce1"></div>
        <div className="bounce2"></div>
        <div className="bounce3"></div>
      </div>
    );
  };

  const renderCheckboxList = ({
    facilityData,
    checkedValues,
    handleCheckboxChange,
  }) => {
    return (
      <FixedSizeList height={290} itemSize={40} itemCount={facilityData.length}>
        {({ index, style }) => (
          <div style={style}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedValues.includes(
                    facilityData[index].sendingFacilityId.toString()
                  )}
                  disabled={
                    checkedValues.length > 3 &&
                    !checkedValues.includes(
                      facilityData[index].sendingFacilityId.toString()
                    )
                  }
                  onChange={handleCheckboxChange}
                  name={facilityData[index].sendingFacilityName}
                  value={facilityData[index].sendingFacilityId}
                />
              }
              label={`${facilityData[index].sendingFacilityName}, ${facilityData[index].clia}`}
            />
          </div>
        )}
      </FixedSizeList>
    );
  };

  const sortedFacilityData = [...facilityData].sort((a, b) => {
    if (a.sendingFacilityName < b.sendingFacilityName) return -1;
    if (a.sendingFacilityName > b.sendingFacilityName) return 1;
    return 0;
  });

  const showAlertMessage = (message) => {
    const dateRangeRegex =
      /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}) to (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3})/;
    const matches = message.match(dateRangeRegex);

    if (matches && matches.length >= 3) {
      const startDateString = matches[1];
      const endDateString = matches[2];

      const startDate = moment(startDateString);
      const endDate = moment(endDateString);

      if (startDate.isValid() && endDate.isValid()) {
        const formattedStartDate = startDate.format("YYYY-MM-DD HH:mm");
        const formattedEndDate = endDate.format("YYYY-MM-DD HH:mm");

        return `No transaction record found with date range ${formattedStartDate} to ${formattedEndDate}`;
      } else {
        return "Invalid date format in API response";
      }
    } else {
      return message;
    }
  };

  return (
    <>
      <div className="flexRow">
        {statisticsDataByFacility?.length === 0 ? (
          <Spinner />
        ) : (
          <ResponsiveContainer width={700} height={350}>
            <BarChart
              width={500}
              data={statisticsDataByFacility}
              margin={{
                top: 20,
                left: 20,
                bottom: 60,
              }}
            >
              <XAxis
                dataKey="sendingFacility"
                dy={16}
                tickFormatter={
                  statisticsDataByFacility && statisticsDataXAxisName
                }
              />
              <YAxis dx={-16} />
              <Tooltip cursor={{ fill: "transparent" }} />
              <Bar
                isAnimationActive={false}
                dataKey="messagesProcessed"
                stackId="a"
                barSize={40}
                fill="#0839AD"
              />
              <Bar
                isAnimationActive={false}
                dataKey="messagesWithError"
                stackId="a"
                barSize={40}
                fill="#0365C0"
              />
              <Bar
                isAnimationActive={false}
                dataKey="messagesInProgress"
                stackId="a"
                barSize={40}
                fill="#1CA6DF"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
        <Col className="topChartCardContainer">
          <Card className="topChartCard">
            <ListGroup variant="flush">
              <ListGroup.Item className="border-0 itemStyle">
                <Col sm={1}>
                  <div className="lighter-blue-square"></div>
                </Col>
                <Col className="offset-1">
                  <div>Messages in Progress</div>
                </Col>
                <Col sm={1} className="text-end">
                  <div>
                    {statisticsDataByFacility?.length > 0 &&
                      statisticsDataByFacility.reduce(
                        (sum, item) => sum + item.messagesInProgress,
                        0
                      )}
                  </div>
                </Col>
              </ListGroup.Item>
              <ListGroup.Item className="border-0 itemStyle">
                <Col sm={1}>
                  <div className="light-blue-square"></div>
                </Col>
                <Col className="offset-1">
                  <div>Messages with Error</div>
                </Col>
                <Col sm={1} className="text-end">
                  <div>
                    {statisticsDataByFacility?.length > 0 &&
                      statisticsDataByFacility.reduce(
                        (sum, item) => sum + item.messagesWithError,
                        0
                      )}
                  </div>
                </Col>
              </ListGroup.Item>
              <ListGroup.Item className="border-0 itemStyle">
                <Col sm={1}>
                  <div className="blue-square"></div>
                </Col>
                <Col className="offset-1">
                  <div>Messages Processed</div>
                </Col>
                <Col sm={1} className="text-end">
                  <div>
                    {statisticsDataByFacility?.length > 0 &&
                      statisticsDataByFacility.reduce(
                        (sum, item) => sum + item.messagesProcessed,
                        0
                      )}
                  </div>
                </Col>
              </ListGroup.Item>
              <ListGroup.Item className="border-0 itemStyle">
                <div className="bold">
                  {`Total Messages by Facilities (Last 4 Weeks)`}
                </div>
                <div className="bold">
                  {statisticsDataByFacility?.length > 0 &&
                    statisticsDataByFacility.reduce(
                      (sum, item) =>
                        sum +
                        item.messagesInProgress +
                        item.messagesProcessed +
                        item.messagesWithError,
                      0
                    )}
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
      </div>
      <div style={{ marginLeft: "75px", maxWidth: "95%" }}>
        <Card
          style={{
            paddingTop: "20px",
            paddingLeft: "10px",
            minHeight: "350px",
            marginBottom: "35px",
          }}
        >
          <Row>
            <Col sm={6}>
              <ListGroup.Item className="border-0">
                <div
                  style={{ marginLeft: "10px", marginBottom: "10px" }}
                  className="bold"
                >
                  Sending Facilities
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="border-0 itemStyle">
                {facilityData.length === 0 ? (
                  <div lassName="form-container">{showSpinner()}</div>
                ) : (
                  <div
                    className="form-container"
                    style={{ overflow: "hidden" }}
                  >
                    {renderCheckboxList({
                      facilityData: sortedFacilityData, // Pass the sorted facilityData
                      checkedValues,
                      handleCheckboxChange,
                    })}
                  </div>
                )}
              </ListGroup.Item>
            </Col>
            <Col style={{ marginLeft: "40px", marginTop: "20px" }}>
              <ListGroup.Item>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="radio-buttons-group"
                  onChange={handleRadioButtonChange}
                  value={radioOptionButton}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label="Received by Day (Today)"
                  />
                  <FormControlLabel
                    value="7"
                    control={<Radio />}
                    label="Received by Week (Last 7 Days)"
                  />
                  <FormControlLabel
                    value="28"
                    control={<Radio />}
                    label="Total Messages Received by Facilities (Last 4 weeks)"
                  />
                </RadioGroup>
              </ListGroup.Item>
              <Row style={{ marginTop: "60px" }}>
                <Col sm={3}>
                  <div>
                    <Button className="tableFilterByBtn " onClick={handleClear}>
                      {"Clear"}
                    </Button>
                  </div>
                </Col>
                <Col sm={4}>
                  <Button
                    className="tableFilterByBtn "
                    disabled={radioOptionButton === ""}
                    onClick={handleExecute}
                  >
                    <img
                      src={`${validateAndSanitizeUrl(S3_URL)}filter-icon.png`}
                      alt=""
                      style={{ marginRight: "10px" }}
                    ></img>
                    {"Execute"}
                  </Button>
                </Col>
              </Row>
              <Row>
                {alertMessage ? (
                  <div style={{ marginTop: "30px", color: "#842029" }}>
                    {showAlertMessage(alertMessage)}
                  </div>
                ) : checkedValues.length > 3 ? (
                  <div style={{ marginTop: "30px", color: "#842029" }}>
                    Only up to 4 Sending Facilities can be selected.
                  </div>
                ) : (
                  ""
                )}
              </Row>
            </Col>
          </Row>
        </Card>
      </div>
    </>
  );
};

export default DashboardChartMessageStatisticsByFacility;
